/**
 * Primitives shared by every /docs page: copyable code blocks, the live
 * "are you connected?" banner, and a small inline-callout helper.
 * Kept beside the docs pages because they're docs-specific — nothing
 * else in the portal needs them.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { api, ApiError } from '../../api/client.js';
import type { OAuthGrantSummary } from '../../api/types.js';
import { IconInfo } from '../../components/Icons.js';
import { Banner, Card, CopyButton } from '../../components/ui.js';

// Re-exported so existing docs pages can keep importing CopyButton from here;
// the canonical implementation now lives in ui.tsx (single source of truth).
export { CopyButton };

interface CodeBlockProps {
  /** Optional tab/section label rendered above the snippet (e.g. file path). */
  caption?: string;
  /** The exact text to display + copy. */
  body: string;
  /** Suppress the copy button (useful for short examples). */
  noCopy?: boolean;
  /** Max height before scrolling; defaults to 24rem. */
  maxHeight?: string;
}

export function CodeBlock({ caption, body, noCopy, maxHeight = '24rem' }: CodeBlockProps) {
  return (
    <div className="rounded-xl border border-line bg-surface overflow-hidden my-3">
      {(caption || !noCopy) && (
        <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-line bg-surface-2/40">
          <span className="text-xs uppercase tracking-wider text-muted font-medium truncate">
            {caption ?? ''}
          </span>
          {!noCopy && <CopyButton text={body} />}
        </div>
      )}
      <pre
        className="px-4 py-3 text-xs overflow-auto font-mono leading-relaxed whitespace-pre-wrap text-ink-2"
        style={{ maxHeight }}
      >
        {body}
      </pre>
    </div>
  );
}

/**
 * Live "is the AI actually connected?" check. Hits /api/mcp/grants —
 * one grant per OAuth-authorized AI client. > 0 grants means the
 * end-to-end MCP + OAuth handshake has completed at least once on
 * this account.
 */
export function ConnectionStatus() {
  const [state, setState] = useState<{
    loading: boolean;
    grants?: OAuthGrantSummary[];
    error?: string;
  }>({ loading: true });

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ grants: OAuthGrantSummary[] }>('/api/mcp/grants')
      .then((res) => {
        if (!cancelled) setState({ loading: false, grants: res.grants });
      })
      .catch((err) => {
        if (!cancelled) {
          setState({
            loading: false,
            error: err instanceof ApiError ? err.message : String(err),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.loading) {
    return <Banner tone="info">Checking your connection…</Banner>;
  }
  if (state.error) {
    return <Banner tone="error">Couldn&apos;t check connection status: {state.error}</Banner>;
  }
  const count = state.grants?.length ?? 0;
  if (count === 0) {
    return (
      <Banner tone="warn">
        No AI client has signed in to Kin on your account yet. Follow the steps below to connect
        one.
      </Banner>
    );
  }
  return (
    <Banner tone="success">
      Connected — {count} AI client{count === 1 ? '' : 's'} authorized to act as you on Kin. Manage
      them on{' '}
      <a className="underline" href="/setup">
        Setup
      </a>
      .
    </Banner>
  );
}

interface CalloutProps {
  tone?: 'info' | 'tip';
  title?: ReactNode;
  children: ReactNode;
}

/**
 * Inline aside for "tip" or "note" boxes inside long-form copy. Tone
 * differs from Banner — Callout is a content element, Banner is a
 * status indicator. Same token system either way.
 */
export function Callout({ tone = 'info', title, children }: CalloutProps) {
  const wrap =
    tone === 'tip' ? 'border-brand/20 bg-brand-soft/30' : 'border-info/20 bg-info-soft/40';
  const accent = tone === 'tip' ? 'text-brand' : 'text-info';
  return (
    <div className={`rounded-xl border px-4 py-3 my-4 flex gap-3 items-start ${wrap}`}>
      <IconInfo size={16} className={`mt-0.5 shrink-0 ${accent}`} />
      <div className="flex-1 min-w-0 text-sm space-y-1.5 text-ink-2">
        {title && <p className={`font-semibold tracking-tight ${accent}`}>{title}</p>}
        {children}
      </div>
    </div>
  );
}

/**
 * Section container — a Card with a heading and an introductory paragraph
 * built in. Used by every docs page so they all hang together visually.
 */
export function DocSection({
  id,
  title,
  intro,
  children,
}: {
  id?: string;
  title: ReactNode;
  intro?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card className="mb-6">
      <div className="px-5 sm:px-6 pt-5 pb-2">
        <h2 id={id} className="font-display text-lg font-semibold tracking-tight scroll-mt-24">
          {title}
        </h2>
        {intro && <p className="text-muted text-sm mt-1.5">{intro}</p>}
      </div>
      <div className="px-5 sm:px-6 pb-5 pt-2 space-y-3 text-sm text-ink-2 leading-relaxed">
        {children}
      </div>
    </Card>
  );
}

/**
 * Three-up grid that links to other docs sections. Used on the Overview
 * page; each card has a title, blurb, and a `to` route.
 */
export function DocCardGrid({
  cards,
}: {
  cards: { to: string; title: string; blurb: string; icon: ReactNode }[];
}) {
  return (
    <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {cards.map((c) => (
        <li key={c.to}>
          <a
            href={c.to}
            className="group block h-full rounded-xl border border-line bg-surface p-5 hover:border-brand/40 hover:shadow-card transition-all"
          >
            <div className="flex items-center gap-3 mb-2">
              <span className="h-9 w-9 rounded-lg bg-brand-soft text-brand flex items-center justify-center group-hover:scale-105 transition-transform">
                {c.icon}
              </span>
              <h3 className="font-display font-semibold text-base text-ink group-hover:text-brand transition-colors">
                {c.title}
              </h3>
            </div>
            <p className="text-muted text-sm">{c.blurb}</p>
          </a>
        </li>
      ))}
    </ul>
  );
}
